import {
  CalendarCheck2,
  CalendarX2,
  CheckCircle2,
  Clock3,
} from "lucide-react";

import type {
  HouseholdIntelligenceMember,
  HouseholdIntelligenceResponse,
} from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";

type Props = {
  intelligence: HouseholdIntelligenceResponse;
};

export function HouseholdAttendanceCard({ intelligence }: Props) {
  const members = intelligence.members;
  const membersWithAttendance = members.filter(
    (member) => member.last_attended_at
  );
  const membersWithoutAttendance = members.length - membersWithAttendance.length;

  const mostRecentAttendance = getMostRecentAttendance(members);

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-emerald-500/15 p-3 text-emerald-400">
            <CalendarCheck2 size={22} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Household Attendance
            </h2>

            <p className="text-sm text-slate-500">
              Attendance visibility across household members
            </p>
          </div>
        </div>

        <div className="text-left sm:text-right">
          <p className="text-3xl font-bold text-emerald-400">
            {intelligence.attendance_rate}%
          </p>

          <p className="text-xs text-slate-500">
            Household attendance rate
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <AttendanceMetric
          icon={<CheckCircle2 size={18} />}
          label="Attendance Recorded"
          value={membersWithAttendance.length}
          className="text-emerald-400"
        />

        <AttendanceMetric
          icon={<CalendarX2 size={18} />}
          label="No Attendance Yet"
          value={membersWithoutAttendance}
          className="text-amber-400"
        />

        <AttendanceMetric
          icon={<Clock3 size={18} />}
          label="Most Recent"
          value={
            mostRecentAttendance
              ? formatDate(mostRecentAttendance)
              : "None"
          }
          className="text-indigo-400"
        />
      </div>

      {members.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
          <p className="font-medium text-white">
            No household members
          </p>

          <p className="mt-2 text-sm text-slate-400">
            Assign members to this household to begin tracking attendance.
          </p>
        </div>
      ) : (
        <div className="mt-6 divide-y divide-slate-800 overflow-hidden rounded-2xl border border-slate-800">
          {sortMembersByAttendance(members).map((member) => (
            <div
              key={member.id}
              className="flex flex-col gap-3 bg-slate-950/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="font-medium text-white">
                  {member.first_name} {member.last_name}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {member.relationship_to_household ||
                    "Household member"}
                </p>
              </div>

              <div className="sm:text-right">
                {member.last_attended_at ? (
                  <>
                    <p className="text-sm font-medium text-emerald-300">
                      Last attended {formatDate(member.last_attended_at)}
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      {formatRelativeDate(member.last_attended_at)}
                    </p>
                  </>
                ) : (
                  <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-300">
                    No attendance recorded
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AttendanceMetric({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  className: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
      <div className={`flex items-center gap-2 ${className}`}>
        {icon}

        <p className="text-xs font-medium uppercase tracking-wider">
          {label}
        </p>
      </div>

      <p className="mt-3 text-xl font-bold text-white">{value}</p>
    </div>
  );
}

function getMostRecentAttendance(
  members: HouseholdIntelligenceMember[]
) {
  const timestamps = members
    .map((member) => member.last_attended_at)
    .filter((value): value is string => Boolean(value))
    .sort(
      (a, b) =>
        new Date(b).getTime() - new Date(a).getTime()
    );

  return timestamps[0] ?? null;
}

function sortMembersByAttendance(
  members: HouseholdIntelligenceMember[]
) {
  return [...members].sort((a, b) => {
    if (!a.last_attended_at && !b.last_attended_at) {
      return a.last_name.localeCompare(b.last_name);
    }

    if (!a.last_attended_at) {
      return 1;
    }

    if (!b.last_attended_at) {
      return -1;
    }

    return (
      new Date(b.last_attended_at).getTime() -
      new Date(a.last_attended_at).getTime()
    );
  });
}

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelativeDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const differenceInDays = Math.floor(
    (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (differenceInDays <= 0) {
    return "Today";
  }

  if (differenceInDays === 1) {
    return "Yesterday";
  }

  if (differenceInDays < 7) {
    return `${differenceInDays} days ago`;
  }

  if (differenceInDays < 30) {
    const weeks = Math.floor(differenceInDays / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }

  const months = Math.floor(differenceInDays / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}